import axios from "axios";
import { useEffect, useState } from "react";
import { FiEdit, FiMapPin, FiTrash2 } from "react-icons/fi";
import { MdToggleOff, MdToggleOn } from "react-icons/md";
import Breadcrumb from "../../../components/AdminSideComponents/BreadCrumb";
import GoogleMapPicker from "../../../components/UserSideComponents/bookings/GoogleMapPicker";
import {
  confirmAction,
  showError,
  showSuccess,
} from "../../../utils/toastUtils";

const MAP_PREVIEW = (lat, lng) =>
  `https://maps.googleapis.com/maps/api/staticmap?center=${lat},${lng}&zoom=15&size=400x200&markers=color:red%7C${lat},${lng}&key=${process.env.REACT_APP_GOOGLE_MAP_KEY}`;

const Branch = () => {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);

  const fetchBranches = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/branches/get-all`,
      );
      setBranches(res.data?.data || []);
    } catch {
      showError("Failed to fetch branches");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBranches();
  }, []);

  const toggleStatus = async (branch) => {
    const confirmed = await confirmAction({
      title: `Mark branch as ${branch.isActive ? "inactive" : "active"}?`,
      text: "This will affect branch availability",
      confirmText: "Yes",
    });

    if (!confirmed) return;

    try {
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/branches/${branch._id}`,
        { isActive: !branch.isActive },
      );

      setBranches((prev) =>
        prev.map((b) =>
          b._id === branch._id ? { ...b, isActive: !b.isActive } : b,
        ),
      );

      showSuccess("Branch status updated");
    } catch {
      showError("Failed to update branch status");
    }
  };

  const deleteBranch = async (branch) => {
    const confirmed = await confirmAction({
      title: "Delete this branch?",
      text: "This action cannot be undone",
      confirmText: "Delete",
    });

    if (!confirmed) return;

    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/branches/${branch._id}`,
      );
      showSuccess("Branch deleted");
      fetchBranches();
    } catch (error) {
      console.error(error?.response || "Failed to delete branch");
      showError(error?.response?.data?.message || "Failed to delete branch");
    }
  };

  return (
    <div className="pb-2 bg-gradient-to-br px-6 min-h-screen from-[#FFF7F3] to-gray-50">
      <div className="py-2">
        <Breadcrumb
          title1="Branches"
          title2="Branch Management"
          showBtn
          buttonName="Add New"
          onBtnClick={() => {
            setEditingBranch(null);
            setOpenModal(true);
          }}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6 mt-6">
        {loading
          ? Array.from({ length: 6 }).map((_, i) => (
            <BranchCard key={i} isLoading />
          ))
          : branches.map((branch) => (
            <BranchCard
              key={branch._id}
              branch={branch}
              onEdit={(b) => {
                setEditingBranch(b);
                setOpenModal(true);
              }}
              onDelete={deleteBranch}
              onToggle={toggleStatus}
            />
          ))}
      </div>

      {openModal && (
        <BranchModal
          branch={editingBranch}
          onClose={() => setOpenModal(false)}
          onSuccess={() => {
            setOpenModal(false);
            fetchBranches();
          }}
        />
      )}
    </div>
  );
};

const BranchCard = ({
  branch,
  onEdit,
  onDelete,
  onToggle,
  isLoading = false,
}) => {
  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl border shadow-sm h-[320px] animate-pulse overflow-hidden">
        <div className="h-32 bg-gray-200" />
        <div className="p-5 space-y-3">
          <div className="h-4 bg-gray-200 rounded w-2/3" />
          <div className="h-3 bg-gray-200 rounded w-full" />
          <div className="h-3 bg-gray-200 rounded w-4/5" />
          <div className="flex justify-between pt-4">
            <div className="h-9 w-24 bg-gray-200 rounded-lg" />
            <div className="h-9 w-24 bg-gray-200 rounded-lg" />
          </div>
        </div>
      </div>
    );
  }

  const [lng, lat] = branch.location?.coordinates || [];

  return (
    <div className="bg-white rounded-2xl shadow-xl border hover:shadow-2xl transition h-[20rem] flex flex-col overflow-hidden">
      <div className="relative h-44 w-full bg-gray-100">
        {branch.sequence !== undefined && (
          <div className="absolute top-2 left-2 bg-white/90 backdrop-blur px-2 py-1 rounded-md text-xs font-semibold text-primaryColor shadow-lg">
            #{branch.sequence}
          </div>
        )}
        {lat && lng ? (
          <img
            src={MAP_PREVIEW(lat, lng)}
            alt="Branch location"
            className="h-full w-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="h-full flex items-center justify-center text-gray-400">
            <FiMapPin className="text-3xl" />
          </div>
        )}

        <button
          onClick={() => onToggle(branch)}
          className="absolute top-2 right-2 bg-white/90 rounded-full p-1 shadow"
        >
          {branch.isActive ? (
            <MdToggleOn className="text-4xl text-green-500" />
          ) : (
            <MdToggleOff className="text-4xl text-gray-400" />
          )}
        </button>
      </div>

      <div className="p-5 flex flex-col justify-between flex-1">
        <div className="space-y-1">
          <h3 className="text-lg font-bold text-gray-800 line-clamp-1">
            {branch.branchName}
          </h3>

          <p className="text-sm text-gray-600 line-clamp-1">
            {branch.address.street}
          </p>

          <p className="text-xs text-gray-500 line-clamp-1">
            {branch.address.city}, {branch.address.state} –{" "}
            {branch.address.pincode}
          </p>
        </div>

        <div className="flex justify-between pt-2 mt-2 border-t ">
          <button
            onClick={() => onEdit(branch)}
            className="h-7 w-24 flex items-center justify-center gap-2 rounded-lg text-orange-600 hover:bg-orange-50 transition text-sm font-medium"
          >
            <FiEdit />
            Edit
          </button>

          <button
            onClick={() => onDelete(branch)}
            className="h-7 w-24 flex items-center justify-center gap-2 rounded-lg text-red-600 hover:bg-red-50 transition text-sm font-medium"
          >
            <FiTrash2 />
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

const BranchModal = ({ branch, onClose, onSuccess }) => {
  const [branchName, setBranchName] = useState("");
  const [sequence, setSequence] = useState("");
  const [address, setAddress] = useState({
    street: "",
    city: "",
    state: "",
    pincode: "",
  });
  const [location, setLocation] = useState({
    type: "Point",
    coordinates: [],
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (branch) {
      setBranchName(branch.branchName);
      setAddress(branch.address || {});
      setSequence(branch.sequence ?? "");
      setLocation(branch.location || { type: "Point", coordinates: [] });
    }
  }, [branch]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!branchName || location.coordinates.length !== 2) {
      return showError("Branch name & location are required");
    }

    setLoading(true);

    const payload = {
      branchName,
      address,
      location,
      sequence: sequence ? Number(sequence) : undefined,
    };

    try {
      if (branch) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/branches/${branch._id}`,
          payload,
        );
        showSuccess("Branch updated");
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/branches/create`,
          payload,
        );
        showSuccess("Branch created");
      }

      onSuccess();
    } catch (err) {
      showError(err?.response?.data?.message || "Operation failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
      <div className="bg-white rounded-2xl w-full max-w-2xl p-6">
        <h2 className="text-xl font-bold mb-4">
          {branch ? "Edit Branch" : "Add New Branch"}
        </h2>

        <div className="space-y-4">
          <div className="flex items-end gap-3">
            <div className="flex flex-col w-full">
              <label className="text-xs font-semibold text-gray-500 mb-1 tracking-wide">
                Branch Name
              </label>

              <input
                type="text"
                placeholder="Enter branch name"
                value={branchName}
                onChange={(e) => setBranchName(e.target.value)}
                className="w-full h-[42px] rounded-lg px-3 text-sm outline-none ring-1 ring-gray-300 focus:ring-2 focus:ring-primaryColor transition"
              />
            </div>
            <div className="flex flex-col">
              <label className="text-xs font-semibold text-gray-500 mb-1 tracking-wide">
                Display Order
              </label>

              <input
                type="number"
                min="0"
                placeholder="e.g. 1"
                value={sequence}
                onChange={(e) => setSequence(e.target.value)}
                className="w-28 h-[42px] rounded-lg px-3 text-sm outline-none ring-1 ring-gray-300 focus:ring-2 focus:ring-primaryColor transition"
              />
            </div>
          </div>

          <GoogleMapPicker
            location={location}
            onChange={({ coordinates, address }) => {
              setLocation({ type: "Point", coordinates });
              setAddress(address);
            }}
          />

          <div className="flex justify-end gap-3 pt-4">
            {/* LEFT SIDE - Sequence Input */}

            <div className="flex gap-3">

              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="px-5 py-2 rounded-lg bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading || !location.coordinates.length}
                className={`px-5 py-2 rounded-lg text-white font-medium transition
                ${loading || !location.coordinates.length
                    ? "bg-brandAccentColor text-gray-400 cursor-not-allowed"
                    : "bg-primaryColor hover:opacity-90 active:scale-[0.98]"
                  }`}
              >
                {loading
                  ? "Processing..."
                  : branch
                    ? "Save Changes"
                    : "Create Branch"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Branch;
